import { ApplicationController } from '@aha-app/mvc';
import WidgetsController from '../../../../controllers/WidgetsController';
import type TimerWidget from '../../../../widgets/TimerWidget';
import { TimerStatus } from '../../../../widgets/TimerWidget';
import trackEvent from '../../../../utils/analytics';

import {
  MAX_TIME,
  MIN_TIME,
  MINUTE,
  SECOND,
  DEFAULT_DURATION,
  formattedTime,
  timeInMillis,
} from '../../../../utils/time';

const SECONDS_REGEX = /^\s*(\d+)\s*$/;
const SHORTHAND_REGEX = /^\s*((\d+)m)?\s*((\d+)s)?\s*$/;
const DIGITAL_REGEX = /^\s*(\d+):?([0-5]\d)\s*$/;

const MIN_INTERVAL = 5 * SECOND;
const SMALL_INTERVAL = 30 * SECOND;
const LARGE_INTERVAL = MINUTE;
const MAX_INTERVAL = 5 * MINUTE;

const INTERVAL_RANGES = [
  [MINUTE, MIN_INTERVAL],
  [5 * MINUTE, SMALL_INTERVAL],
  [15 * MINUTE, LARGE_INTERVAL],
  [MAX_TIME, MAX_INTERVAL],
];

interface State {
  startDuration: number;
  inputText: string;
  typing: boolean;
}

interface ControllerProps {
  startDuration?: number;
  widget?: TimerWidget;
}

class TimerInputController extends ApplicationController<
  State,
  ControllerProps
> {
  widget?: TimerWidget;
  widgetsController: WidgetsController;

  static initialState = {
    startDuration: DEFAULT_DURATION,
    inputText: formattedTime(DEFAULT_DURATION),
    typing: false,
  };

  async initialize({ startDuration, widget }: ControllerProps): Promise<void> {
    if (startDuration) this.state.startDuration = startDuration;

    this.state.inputText = formattedTime(this.state.startDuration);
    this.widgetsController = this.findControllerInstance(WidgetsController)!;
    this.widget = widget;
  }

  changeProps({ startDuration, widget }) {
    if (startDuration && startDuration !== this.state.startDuration) {
      this.state.startDuration = startDuration;
      this.state.inputText = formattedTime(startDuration);
    }

    if (widget && !this.widget) {
      this.widget = widget;
    }
  }

  actionSetStartDuration(newDuration: number) {
    this.state.startDuration = Math.max(
      MIN_TIME,
      Math.min(newDuration, MAX_TIME)
    );
    this.state.inputText = formattedTime(this.state.startDuration);
  }

  actionSetInputText(text: string) {
    this.state.inputText = text;
    this.state.typing = true;
  }

  actionStartTimer(): void {
    if (this.state.typing) this.actionUpdateFromInput();

    const data = {
      status: TimerStatus.Active,
      startDuration: this.state.startDuration,
      exactDuration: timeInMillis(this.state.startDuration),
      startedAt: Date.now(),
    };

    if (!this.widget) {
      const widget = this.widgetsController.actionAddWidget(
        'TimerWidget',
        data
      ) as TimerWidget;
      this.widget = widget;
    } else {
      this.widgetsController.actionUpdateWidget(this.widget, data);
    }

    trackEvent('Feature-7236748624623262225', 'User starts whiteboard timer', {
      Duration: this.state.startDuration,
    });
  }

  actionIncrementDuration(positive: boolean): void {
    const amountToChange = this.incrementAmount(positive);
    this.actionSetStartDuration(this.state.startDuration + amountToChange);
  }

  // Returns the amount of seconds to take you to the closest time target
  // in either the positive (increasing time) or negative (decreasing time) direction.
  //
  // Below 1 min, time targets are every 5 seconds. Up to 5 minutes they are every 30s,
  // up to 15 minutes they are every minute, and every 5 minutes after that.
  incrementAmount(positive: boolean): number {
    for (const range of INTERVAL_RANGES) {
      const max = range[0];

      if (
        this.state.startDuration < max ||
        (this.state.startDuration === max && !positive)
      ) {
        const interval = range[1];
        const remainder = this.state.startDuration % interval;

        if (positive) {
          return interval - remainder;
        } else if (remainder === 0) {
          return -interval;
        } else {
          return -remainder;
        }
      }
    }

    return 0; // Not possible
  }

  // Supports three notations:
  // - digital (eg. 1:30 - the colon is optional)
  // - integer (eg. 90 - integer number of seconds)
  // - shorthand (eg. 1m 30s)
  actionUpdateFromInput(time: string = this.state.inputText): void {
    let minutes, seconds;
    let match = time.match(DIGITAL_REGEX);

    // Ordering matters: digital and second regexes overlap, shorthand can match an empty string
    if (match) {
      minutes = match[1];
      seconds = match[2];
    } else if ((match = time.match(SECONDS_REGEX))) {
      seconds = match[1];
    } else if ((match = time.match(SHORTHAND_REGEX))) {
      minutes = match[2];
      seconds = match[4];
    }

    // Check if no match or all values undefined (if matched a whitespace-only string)
    if (!match || !(seconds || minutes)) {
      this.state.typing = false;
      this.state.inputText = formattedTime(this.state.startDuration);
    }

    const segments = [seconds, minutes];
    const newDuration = segments.reduce((result, num, index) => {
      if (!num) return result;

      const parsedNum = parseInt(num);
      const multiplier = Math.pow(MINUTE, index);

      return result + parsedNum * multiplier;
    }, 0);

    this.actionSetStartDuration(newDuration);
    this.state.typing = false;
  }
}

export default TimerInputController;
